/**
 * The day's movements.
 *
 * Every column on this board is one of the three times or is derived from
 * them, and the board says which one it is showing. An airport display with a
 * single unlabelled time column is the commonest way a passenger, a handler
 * and a duty manager are all told different things by the same screen.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import {
  bestKnown,
  byHour,
  countDay,
  delayMinutes,
  estimateStale,
  flightName,
  peakHour,
  punctuality,
  punctualityOf,
  punctualitySentence,
  PUNCTUALITY_LABEL,
  SERVICE_LABEL,
  type Flight,
} from "../../domain/flights";
import { useFlightWorld } from "./useFlightWorld";
import { Card, EmptyState, Field, Segmented, Select, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useSearchParams } from "react-router-dom";
import { doorsLateBy, opsState, SOURCE_LABEL } from "../../domain/flightDelays";
import { DayNotes, DayState, FlightTheme, hasDay, LiveStrip, OpsLegend, OpsPill } from "./FlightTheme";
import { HourlyChart } from "./HourlyChart";
import { useScreenContext } from "../../assistant/screen";
import { Tiles } from "../executive/shared";
import { t, tn } from "../../i18n";
import s from "./Flights.module.css";
import { AERODROME_OFFSET_HOURS, hhmm } from "../../lib/format";

/** The note the day used to be held under. Nothing reads or writes it now; kept for the imports that name it. */
export const FLIGHTS_TOPIC = "ops.flights";

/** Riyadh. The operating day is the airport's, not the browser's. */

export function FlightsPage() {
  const session = useSession();
  /* the flight record for today's service date: what the feeds, the schedule and people wrote */
  /* "today's flights": the day's totals stay on today's service date */
  const all = useFlightWorld();
  const world = { ...all, flights: all.today };
  const flights = world.flights;
  const [dir, setDir] = useState<"all" | "arrival" | "departure">("all");
  const [params] = useSearchParams();
  const [show, setShow] = useState<"all" | "late" | "unexplained" | "notyet" | "doors">(() =>
    ["late", "unexplained", "notyet", "doors"].includes(params.get("show") ?? "")
      ? (params.get("show") as "doors")
      : "all",
  );
  const now = new Date();

  const counts = useMemo(() => countDay(flights), [flights]);
  const report = useMemo(() => punctualityOf(flights), [flights]);
  const bars = useMemo(() => byHour(flights, AERODROME_OFFSET_HOURS), [flights]);
  const peak = peakHour(bars);

  const shown = useMemo(
    () =>
      flights
        .filter((f) => f.source !== "forecast")
        .filter((f) => dir === "all" || f.direction === dir)
        .filter((f) =>
          show === "all"
            ? true
            : show === "late"
              ? punctuality(f) === "late"
              : show === "unexplained"
                ? punctuality(f) === "late" && !f.delayCode
                : show === "doors"
                  ? opsState(f, new Date()) === "door_late"
                  : !f.actualAt && !f.cancelled,
        )
        .sort((a, b) => (bestKnown(a).at ?? "").localeCompare(bestKnown(b).at ?? "")),
    [flights, dir, show],
  );

  const tiles = [
    {
      key: "movements",
      label: t("movements today"),
      value: counts.arrivals + counts.departures,
      unit: "",
      text: t("{a} arrivals, {d} departures", { a: counts.arrivals, d: counts.departures }),
      tone: "good" as const,
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
    {
      key: "moved",
      label: t("have happened"),
      value: counts.moved,
      unit: "",
      text: t("{n} still to come", { n: counts.arrivals + counts.departures - counts.moved }),
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
    {
      key: "ontime",
      label: t("on time"),
      value: report.onTimePct,
      unit: "%",
      text:
        report.measured === 0
          ? t("nothing has moved yet")
          : t("of {n} measured", { n: tn(report.measured, "{n} movement", "{n} movements") }),
      tone: report.onTimePct === null ? undefined : report.onTimePct >= 80 ? ("good" as const) : ("warn" as const),
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
    {
      key: "unexplained",
      label: t("late with no reason recorded"),
      value: report.unexplained,
      unit: "",
      text:
        report.unexplained === 0 ? t("every late movement carries a delay code") : t("of {n} late", { n: report.late }),
      tone: report.unexplained > 0 ? ("bad" as const) : ("good" as const),
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
    {
      key: "pax",
      label: t("passengers"),
      value: counts.passengers,
      unit: "",
      text:
        counts.passengersUnknown > 0
          ? t("{n} movements file no passenger figure", { n: counts.passengersUnknown })
          : t("every movement files a figure"),
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
    {
      key: "peak",
      label: t("busiest hour"),
      value: peak ? peak.total : null,
      unit: "",
      text: peak
        ? t("{h}:00 — {a} in, {d} out", { h: String(peak.hour).padStart(2, "0"), a: peak.arrivals, d: peak.departures })
        : t("nothing scheduled"),
      assumed: true,
      sources: [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }],
    },
  ];

  const board = [
    t("{site}: {a} arrivals and {d} departures today, {moved} of them flown.", {
      site: session.site.code,
      a: counts.arrivals,
      d: counts.departures,
      moved: counts.moved,
    }),
    punctualitySentence(report),
  ];
  if (counts.cancelled > 0) {
    board.push(tn(counts.cancelled, "{n} movement is cancelled.", "{n} movements are canceled."));
  }

  useScreenContext({
    route: "/flights",
    title: "Today's flights",
    worksInFull: true,
    summary: board,
    figures: new Set(
      [counts.arrivals, counts.departures, counts.moved, report.onTimePct ?? 0, report.unexplained, report.late]
        .map(String)
        .concat(Array.from({ length: 13 }, (_, i) => String(i))),
    ),
    own: (q) => answer(q, flights, report),
    offers: [
      { label: "What is late?", ask: "what is late?" },
      { label: "What has no reason?", ask: "which delays have no code?" },
      { label: "When is the peak?", ask: "when is the busiest hour?" },
    ],
  });

  const lede = (
    <p className={s.lede}>
      {t(
        "Today's arrivals and departures. Each time is labeled scheduled, estimated, or actual.",
      )}
    </p>
  );
  if (!hasDay(world))
    return (
      <FlightTheme>
        <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Today's flights">
          {lede}
          <DayState world={world} />
        </Page>
      </FlightTheme>
    );

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Today's flights">
        <LiveStrip flights={flights} site={session.site.code} />
        {lede}
        <DayNotes world={world} />
        {world.day?.sample && (
          <p className={s.note}>
            {t(
              "Sample schedule. The live flight feed will replace it.",
            )}
          </p>
        )}

        <Tiles items={tiles} currency="SAR" />

        <Card
          collapsible
          title="The day, hour by hour"
          sub="Traffic by hour"
        >
          <HourlyChart flights={flights} offsetHours={AERODROME_OFFSET_HOURS} />
          <p className={s.meta}>
            {t(
              "Arrivals above the line, departures below.",
            )}
          </p>
        </Card>

        <Card
          collapsible
          title="The movements"
          sub={
            <>
              {t("Every flight today, with its latest time")}
              <div style={{ marginTop: 8 }}>
                <OpsLegend />
              </div>
            </>
          }
          actions={
            <div className={s.filters}>
              <Segmented
                ariaLabel={t("Arrivals or departures")}
                value={dir}
                onChange={setDir}
                options={[
                  { value: "all" as const, label: t("Both") },
                  { value: "arrival" as const, label: t("Arrivals") },
                  { value: "departure" as const, label: t("Departures") },
                ]}
              />
              <Field label={t("Show")}>
                <Select value={show} onChange={(e) => setShow(e.target.value as typeof show)} aria-label={t("Show")}>
                  <option value="all">{t("Everything")}</option>
                  <option value="late">{t("Late only")}</option>
                  <option value="unexplained">{t("Late with no reason")}</option>
                  <option value="notyet">{t("Still to come")}</option>
                  <option value="doors">{t("Doors late")}</option>
                </Select>
              </Field>
            </div>
          }
        >
          {shown.length === 0 ? (
            <EmptyState>{t("Nothing matches that.")}</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Route")}</th>
                  <th>{t("Aircraft")}</th>
                  <th className={s.num}>{t("Scheduled")}</th>
                  <th className={s.num}>{t("Estimated")}</th>
                  <th className={s.num}>{t("Actual")}</th>
                  <th className={s.num}>{t("Delay")}</th>
                  <th>{t("Why")}</th>
                </tr>
              </thead>
              <tbody data-testid="flights-board">
                {shown.map((f) => {
                  const d = delayMinutes(f);
                  const state = punctuality(f);
                  const stale = estimateStale(f, now);
                  return (
                    <tr key={f.id} data-testid={`flight-${flightName(f)}`} data-ops={opsState(f, now)}>
                      <td>
                        <b>{flightName(f)}</b>
                        <div className={s.why}>
                          {t(f.direction === "arrival" ? "Arrival" : "Departure")}
                          {f.serviceType && f.serviceType !== "pax" ? ` · ${t(SERVICE_LABEL[f.serviceType])}` : ""}
                        </div>
                      </td>
                      <td>
                        <OpsPill state={opsState(f, now)} />
                        {opsState(f, now) === "door_late" && (
                          <div className={s.bad}>{t("{n} min past door close", { n: doorsLateBy(f, now) ?? 0 })}</div>
                        )}
                        {f.notices?.length ? (
                          <div className={s.why}>
                            {t("told by {src}", { src: t(SOURCE_LABEL[f.notices[f.notices.length - 1].source]) })}
                          </div>
                        ) : null}
                      </td>
                      <td>{f.otherAirport ?? <span className={s.unknown}>—</span>}</td>
                      <td className={s.mono}>
                        {f.aircraftIcao ?? "—"}
                        {f.size && <span className={s.size}>{f.size}</span>}
                      </td>
                      <td className={s.num}>{hhmm(f.scheduledAt)}</td>
                      <td className={`${s.num} ${stale ? s.stale : ""}`}>
                        {f.estimatedAt ? hhmm(f.estimatedAt) : <span className={s.unknown}>—</span>}
                        {stale && <div className={s.why}>{t("not updated")}</div>}
                      </td>
                      <td className={s.num}>
                        {f.actualAt ? <b>{hhmm(f.actualAt)}</b> : <span className={s.unknown}>{t("not yet")}</span>}
                        {f.actualAt && f.actualSource === "manual" && (
                          <div className={s.why} data-testid={`by-hand-${flightName(f)}`}>
                            {f.actualBy ? t("by hand · {who}", { who: f.actualBy }) : t("by hand")}
                          </div>
                        )}
                        {f.actualAt && f.actualSource === "mvt" && <div className={s.why}>{t("from MVT")}</div>}
                      </td>
                      <td className={`${s.num} ${state === "late" ? s.bad : state === "on_time" ? s.good : ""}`}>
                        {f.cancelled ? t(PUNCTUALITY_LABEL.cancelled) : d === null ? "—" : d > 0 ? `+${d}` : String(d)}
                      </td>
                      <td className={s.why}>
                        {f.cancelled ? (
                          f.cancelled.reason
                        ) : f.delayCode ? (
                          <>
                            <span className={s.mono}>{f.delayCode}</span>
                            {f.delayNote ? ` — ${f.delayNote}` : ""}
                          </>
                        ) : state === "late" ? (
                          <span className={s.bad}>{t("no delay code recorded")}</span>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>

        {report.byCode.length > 0 && (
          <Card collapsible title="Where the delay went" sub="by the code somebody entered, uninterpreted">
            <Table>
              <thead>
                <tr>
                  <th>{t("Delay code")}</th>
                  <th className={s.num}>{t("Movements")}</th>
                  <th className={s.num}>{t("Minutes")}</th>
                </tr>
              </thead>
              <tbody data-testid="flights-codes">
                {report.byCode.map((c) => (
                  <tr key={c.code}>
                    <td className={s.mono}>{c.code}</td>
                    <td className={s.num}>{c.count}</td>
                    <td className={s.num}>{c.minutes}</td>
                  </tr>
                ))}
                {report.unexplained > 0 && (
                  <tr>
                    <td className={s.bad}>{t("no code")}</td>
                    <td className={s.num}>{report.unexplained}</td>
                    <td className={s.num}>
                      <span className={s.unknown}>{t("not attributable")}</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        )}

      </Page>
    </FlightTheme>
  );
}

/** What the board can answer without a model. */
function answer(q: string, flights: Flight[], report: ReturnType<typeof punctualityOf>): string | null {
  const text = q.toLowerCase();
  if (/late|delay/.test(text) && /code|reason|why|explain/.test(text)) {
    const none = flights.filter((f) => punctuality(f) === "late" && !f.delayCode);
    return none.length
      ? none.map((f) => `${flightName(f)} — ${delayMinutes(f)} min, ${t("no code")}`).join("\n")
      : t("Every late movement carries a delay code.");
  }
  if (/late|delay/.test(text)) {
    const late = flights.filter((f) => punctuality(f) === "late");
    return late.length
      ? late.map((f) => `${flightName(f)} ${f.otherAirport ?? ""} — ${delayMinutes(f)} min`).join("\n")
      : t("Nothing is late.");
  }
  if (/peak|busiest|busy/.test(text)) {
    const peak = peakHour(byHour(flights, AERODROME_OFFSET_HOURS));
    return peak
      ? t("{h}:00 is the busiest hour: {a} arrivals and {d} departures.", {
          h: String(peak.hour).padStart(2, "0"),
          a: peak.arrivals,
          d: peak.departures,
        })
      : t("Nothing is scheduled.");
  }
  if (/cancel/.test(text)) {
    const off = flights.filter((f) => f.cancelled);
    return off.length
      ? off.map((f) => `${flightName(f)} — ${f.cancelled?.reason}`).join("\n")
      : t("Nothing is canceled.");
  }
  if (/punctual|on time/.test(text)) return punctualitySentence(report);
  return null;
}
